import React, { useMemo } from "react";
import type { MatchTimelineEvent } from "components/matchTimeline";
import MatchTimeline from "components/matchTimeline";
import { getTeamColors } from "components/colors";
import { MatchStatus } from "service";
import { TimelineProgressHeatmap } from "./components/TimelineProgressHeatmap";
import { makeEventIcons } from "./components/EventIcons";
import {
  dedupeEvents,
  decorateTimelineEvent,
  mapRawEventToTimelineEvent,
  mergeAssistsIntoGoals,
  sortScore,
  TimelineEvent,
} from "./util";

const FootballTimeline: React.FC<{ status: MatchStatus }> = ({ status }) => {
  const baseEvents = useMemo(() => {
    const raw = (status as any)?.m;
    if (!Array.isArray(raw)) return [] as TimelineEvent[];

    const unique = dedupeEvents(raw);
    const mapped = unique.map(mapRawEventToTimelineEvent);
    const filtered = mapped.filter(
      (e) =>
        e.minute !== 0 || e.type === "first_half_end" || e.type === "full_time"
    );

    const sorted = [...filtered].sort((a, b) => {
      const sa = sortScore(a);
      const sb = sortScore(b);
      if (sb !== sa) return sb - sa;
      const ta = Number(a?._tm ?? 0);
      const tb = Number(b?._tm ?? 0);
      if (tb !== ta) return tb - ta;
      return 0;
    });

    return mergeAssistsIntoGoals(sorted);
  }, [status]);

  const events = useMemo<MatchTimelineEvent[]>(() => {
    return baseEvents.map((event) => decorateTimelineEvent(event));
  }, [baseEvents]);

  const { home, away } = getTeamColors(status as any);

  const hasFullTime =
    events.some((e) => e.type === "full_time") ||
    String((status as any)?.nsc || "").trim() === "1017" ||
    String((status as any)?.nsc || "").trim() === "1021";

  const hasStarted = events.some(
    (e) =>
      (e.minute ?? 0) > 0 ||
      e.type === "first_half_end" ||
      e.type === "extra_time" ||
      e.type === "full_time"
  );

  const currentMinute = useMemo(() => {
    const latestEvent = events.find((e) => (e.minute ?? 0) > 0);
    return latestEvent?.minute || 0;
  }, [events]);

  const topMarker = hasFullTime
    ? ({ type: "whistle", iconKey: "whistle" } as MatchTimelineEvent)
    : undefined;

  const bottomMarker = hasStarted
    ? ({ type: "whistle", iconKey: "whistle" } as MatchTimelineEvent)
    : undefined;

  const heatmap = (
    <TimelineProgressHeatmap
      events={events}
      homeColor={home}
      awayColor={away}
      status={status}
      currentMinute={currentMinute}
    />
  );

  // Reuse the default icon map but ensure we don't mutate the shared instance
  const timelineIcons = useMemo(() => makeEventIcons(), []);

  return (
    <MatchTimeline
      events={events}
      homeColor={home}
      awayColor={away}
      icons={timelineIcons}
      title="Match Totals"
      header={heatmap}
      topMarker={topMarker}
      bottomMarker={bottomMarker}
    />
  );
};

export default FootballTimeline;
